"use client";

import { FCM_NAVIGATE_KEY, MESSAGE_TYPE } from "@/utils/enum";
import { QK } from "@/utils/queryKeys";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useEffect } from "react";
import useUserUpdate from "./useUserUpdate";

export const handleInvalidateQueries = (
  queryClient: QueryClient,
  url: string,
  onUpdateUser?: () => void
) => {
  const [, rawKey, uid] = url.split("/");
  const key = FCM_NAVIGATE_KEY[rawKey];

  if (!key) {
    onUpdateUser?.();
    return;
  }

  // Special case: chat
  if (key === FCM_NAVIGATE_KEY.chat) {
    queryClient.invalidateQueries({ queryKey: QK.chats });
    if (uid) queryClient.invalidateQueries({ queryKey: QK.chat(uid) });
    return;
  }

  if (key === FCM_NAVIGATE_KEY.plans) {
    if (uid) {
      queryClient.invalidateQueries({ queryKey: QK.subscriptionOrderId(uid) });
      queryClient.invalidateQueries({ queryKey: QK.subscriptionMember() });
    } else {
      onUpdateUser?.();
    }
    return;
  }

  // General
  const queryKey = uid ? [key, uid] : [key];
  queryClient.invalidateQueries({ queryKey });
};

export function useFCMRedirectListener() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { onUpdate } = useUserUpdate();

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator))
      return;

    const handleMessage = (event: MessageEvent) => {
      const msg = event.data;

      if (!msg?.type || !msg?.url) return;

      // Handle navigation from notification click
      if (msg.type === MESSAGE_TYPE.NAVIGATE_TO) {
        const incomingPath = new URL(msg.url, window.location.origin).pathname;
        const currentPath = new URL(
          window.location.href,
          window.location.origin
        ).pathname;

        if (incomingPath !== currentPath) {
          router.push(msg.url);
          return;
        }

        handleInvalidateQueries(queryClient, msg.url, onUpdate);
        return;
      }

      if (msg.type === MESSAGE_TYPE.NOTIFICATION_RECEIVED) {
        handleInvalidateQueries(queryClient, msg.url, onUpdate);
        return;
      }
    };

    navigator.serviceWorker.addEventListener("message", handleMessage);

    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, [router, queryClient]);
}
